import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import moment from "moment/moment";
import Box from "@mui/material/Box";
import {APIGetCustomItemsAll} from "../../listing/api/customItems";
import {IItem} from "../../listing/components/ItemsActive";
import CustomDataTable from "../../../../shared/tables/CustomDataTable";
import GenericButton from "../../../../shared/ui/GenericButton";
import BoxContent from "../../../../shared/ui/BoxContent";
import PageTitle from "../../../../shared/ui/PageTitle";
import CustomButton from "../../../../shared/ui/CustomButton";
import {FilterValue, TableColumnsWithFilter} from "../../../../shared/tables/_interfaces";
import Icon from "../../../../shared/ui/Icon";
import {useAuth} from "../../../../hooks/useAuth";
import useDataTable from "../../../../hooks/useDataTable";
import TestItemCard from "../../../../shared/ui/TestItemCard";

interface TestPreviewCustomItemsListProps {
    createTestData: {
        subject: {
            value: string;
        };
        grade: {
            value: string;
        };
        category: {
            value: string;
        };
        categories: Array<{
            label: string;
            value: string;
            [key: string]: any;
        }>;
    };
    setCustomItemsHandler: (item: IItem) => void;
    removeCustomItemHandler: (item: IItem) => void;
    setSelectedCategoryCustomTestHandler: (value: string) => void;
    selectedItems: IItem[];
}

export const ACTIVE_ITEMS: TableColumnsWithFilter[] = [
    {id: "title", label: "Titlu", noWrap: true, sortable: true, width: "200px", tooltipTextLength: 21, tooltip: true,},
    {id: "subject", label: "Disciplină", noWrap: true, sortable: true, width: "130px"},
    {id: "grade", label: "An", noWrap: true, sortable: true, width: "40px"},
    {id: "subcategory_name", label: "Subcategorie", noWrap: true, sortable: true, tooltip: true, width: "200px", tooltipTextLength: 21, filter: true, filterType: "select"},
    {id: "created_at", label: "Dată", noWrap: true, sortable: true, width: "90px"},
    {id: "id", label: "ID", noWrap: true, sortable: true, numeric: true, width: "70px"},
    {id: "actions", label: "Acțiuni", numeric: true, sticky: true, width: "80px"},
];

const TestPreviewCustomItemsList = ({
    createTestData,
    setCustomItemsHandler,
    removeCustomItemHandler,
    setSelectedCategoryCustomTestHandler,
    selectedItems,
} : TestPreviewCustomItemsListProps) => {
    const { update } = useAuth();
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if(createTestData.category.value !== "" && createTestData.category.value !== undefined) {
            setCurrentFilters({
                subcategory_name: createTestData.category.value,
            });
            setFilterValues({
                subcategory_name: createTestData.category.value,
            });
            setActiveColumnIds(["subcategory_name"]);
        }
        setReady(true);
    }, []);

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        query,
        submittedQuery,
        currentFilters,
        setCurrentFilters,
        orderBy,
        order,
        activeColumnIds,
        setActiveColumnIds,
        filterValues,
        setFilterValues,
        handleFilterChange,
        handleResetFilters,
        handleApplyFilters,
        handleCancelFilters,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "created_at", undefined, "");

    const isItemSelected = useCallback((item: IItem) => {
        return selectedItems.some(
            (selectedItem) => selectedItem.id.toString() === item.id.toString(),
        );
    }, [selectedItems]);

    const previewItemHandler = useCallback((item: IItem) => {
        const selected = isItemSelected(item);

        update("confirm", {
            show: true,
            closable: true,
            title: item.title,
            message: (
                <TestItemCard
                    testMode={7}
                    key={item.id}
                    question={item}
                    isCustomItemPreview={true}
                    isCustomItemPreviewTest = {true}
                    isAnswerReport={false}
                    isPrinting={false}
                    hasBg={true}
                />
            ),
            returnFunction: () => {
                if (selected) {
                    removeCustomItemHandler(item);
                } else {
                    setCustomItemsHandler(item);
                }
            },
            buttonYes: selected ? "Șterge din test" : "Adaugă în test",
            buttonNo: "Închide",
            size: "big"
        });
    }, [isItemSelected, removeCustomItemHandler, setCustomItemsHandler, update]);

    const mapCustomItems = useCallback((item: IItem) => {
        const actions = (
            <Box display="flex" justifyContent="end">
                <Box mr={1}>
                    <GenericButton
                        type="items"
                        onClick={() => {
                            previewItemHandler(item);
                        }}
                    />
                </Box>
                <Box>
                    {isItemSelected(item) ? (
                        <GenericButton type="delete-from-test"  onClick={() => {
                            removeCustomItemHandler(item);
                        }}/>
                    ) : (
                        <GenericButton
                            type="add-small"
                            onClick={() => {
                                setCustomItemsHandler(item);
                            }}
                        />
                    )
                    }
                </Box>
            </Box>
        );

        return {
            id: item.id,
            title: <Link to={`/itemi-proprii/${item.id}`}>{item.title}</Link>,
            subject: item.subject,
            grade: item.grade,
            subcategory_name: item.subcategory_name,
            created_at: moment(item.created_at).locale("ro").format("DD.MM.YYYY"),
            actions,
        }
    }, [removeCustomItemHandler, setCustomItemsHandler, previewItemHandler, isItemSelected]);

    const getCustomItems = useCallback(() => {
        setLoading(true);

        APIGetCustomItemsAll({
            page: `?page=${page + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            subject: createTestData.subject.value,
            grade: createTestData.grade.value,
            subcategory: createTestData.category.value,
        })
            .then((response) => {
                if (response.data.success) {
                    setTableData(response.data.data.data.data);
                    setTotalRows(response.data.data.data.total);
                    setPerPage(response.data.data.data.per_page);
                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(() => update("error", { show: true, code: "A001" }))
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, order, submittedQuery, createTestData.subject.value, createTestData.grade.value, createTestData.category.value, currentFilters.subcategory_name]);

    useEffect(() => {
        if(!ready) return;

      getCustomItems();
    }, [ready, getCustomItems]);

    const addItemHandler = () =>{
        navigate(`/itemi-proprii/adauga`);
    }

    const handleFilterCustomChange = (filterType: string, columnId: string, value: FilterValue) => {
        const newValue = value ? value.toString() : "";
        setSelectedCategoryCustomTestHandler(newValue);
        handleFilterChange(filterType, columnId, value);
    };

    const handleResetCustomFilters = () => {
        setSelectedCategoryCustomTestHandler("");
        handleResetFilters();
    }

    const rows = useMemo(() => {
        return tableData.map((item: IItem) => mapCustomItems(item));
    }, [tableData, mapCustomItems]);

    return (
        <Box width="100%">
            <PageTitle>Selectare itemi</PageTitle>
            <BoxContent>
                <CustomDataTable
                    loading={loading}
                    columns={ACTIVE_ITEMS}
                    rows={rows}
                    rowsPerPage={perPage}
                    page={page}
                    totalRows={totalRows}
                    onPageChange={handleChangePage}
                    handleRequestSort={handleRequestSort}
                    orderBy={orderBy}
                    order={order}
                    hasSearch={true}
                    onChange={handleSearchChange}
                    onSearch={handleSearch}
                    searchValue={query}
                    onClearSearch={handleClearSearch}
                    hasFilters={true}
                    onChangeFilters={handleFilterCustomChange}
                    onCancelFilters={handleCancelFilters}
                    onApplyFilters={handleApplyFilters}
                    activeColumnIds={activeColumnIds}
                    onActiveColumnIdsChange={setActiveColumnIds}
                    resetFormData={handleResetCustomFilters}
                    filterValues={filterValues}
                    filterOptions={{subcategory_name: createTestData.categories}}
                />

                {loading ? (
                    <></>
                ) : (<>{
                    tableData.length === 0 &&
                        <CustomButton
                            onClick={addItemHandler}
                            buttonType="textIcon"
                            bgcolor="dark"
                            icon={<Icon icon="plus" fixedWidth={true} sx={{fontSize: "1.25rem"}}/>}
                        >
                            Adaugă item
                        </CustomButton>
                }</>)}
            </BoxContent>
        </Box>
    )
}

export default TestPreviewCustomItemsList;